//import clsx from 'clsx'
import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'

import { selectCustomFields } from '@SonarCX/slices/customFields'
import { useAppSelector } from '@SonarCX/hooks/redux'
import { FlowType } from '@SonarCX/slices/customFields/flows/helpers'

import useCustomFieldFlows from '@SonarCX/hooks/useCustomFieldFlows'
import CustomFieldInput from '../CustomFieldInput'
import CustomFieldTable from '../CustomFieldTable'

import useStyles from './styles'

type CustomFieldFlowProps = {
  meta: APIMeta[]
  flow: string
  objectType: FlowType
  readOnly?: boolean
  canEdit?: boolean
  isNextStage?: boolean
  handleChange: (
    e: any,
    field: LocalCustomField,
    row?: number,
    rowKey?: string,
    parent?: APICustomField
  ) => void
  handleBlur?: () => void
  setMeta: React.Dispatch<React.SetStateAction<APIMeta[]>>
}

const CustomFieldFlow: React.FC<CustomFieldFlowProps> = ({
  meta,
  flow,
  objectType,
  readOnly,
  canEdit,
  isNextStage,
  handleChange,
  handleBlur,
  setMeta
}) => {
  const { customFields } = useAppSelector(selectCustomFields)
  const { flows } = useCustomFieldFlows(objectType)
  const [metaRows, setMetaRows] = React.useState<APIMeta[]>([])
  const [nextStages, setNextStages] = React.useState<string[]>([])
  const flowStage = React.useMemo(
    () => flows.find(f => f.id === flow),
    [flows, flow]
  )

  const classes = useStyles({ isNextStage })

  // temporal. lol

  React.useEffect(() => {
    if (!flowStage) {
      return
    }
    const { fields } = flowStage

    // Meta items that show up in the fields of this stage
    const metaItemsInFlow = meta.filter(m => fields.some(f => f.id === m.key))

    // Fields in the stage that have an option that potentially has a saved meta value
    const matchingFlowWithOptions = fields.filter(
      f => f.options.length > 0 && metaItemsInFlow.some(x => x.key === f.id)
    )

    let transitioned = false

    matchingFlowWithOptions.forEach((field: APIFlowField) => {
      // Get values related to this field, can be multiple
      const values = metaItemsInFlow.filter(m => field.id === m.key)

      values.forEach(v => {
        // Find option that has triggered a transition if one exists
        const transition = field.options.filter(opt => opt.when === v.value)

        if (transition.length > 0) {
          transitioned = true
          setNextStages(transition.map(t => t.then))
        }
      })
    })

    if (!transitioned) {
      setNextStages([])
    }
  }, [meta, flowStage, flows, flow])

  const [normalFields, flowFields, tableFields] = React.useMemo(() => {
    if (!flowStage || flowStage.fields.length === 0) {
      return [[], []]
    }

    const allFields = flowStage.fields
      .map(field => customFields.find(cf => cf.id === field.id))
      .filter(Boolean)

    const normal = allFields.filter(
      field => field.field_type !== 'flow' && field.field_type !== 'table'
    )
    const flows = allFields.filter(field => field.field_type === 'flow')
    const tables = allFields.filter(field => field.field_type === 'table')
    return [normal, flows, tables]
  }, [flowStage, customFields])

  if (
    !flowStage ||
    normalFields.length + flowFields.length + tableFields.length === 0
  ) {
    return null
  }

  return (
    <React.Fragment>
      {/* <div className={clsx(classes.title, 'cf-title flow-start')}>
        <span>{flowStage.title}</span>
        {flowStage.description && <span>{flowStage.description}</span>}
      </div> */}

      {normalFields.length > 0 && (
        <div className={classes.gridContainer}>
          {normalFields.map(customField => (
            <div key={customField.id} className={classes.gridItem}>
              <CustomFieldInput
                field={customField}
                value={
                  meta.find(data => data.key === customField.id)?.value ?? ''
                }
                disabled={readOnly}
                canEdit={canEdit}
                handleChange={handleChange}
                handleBlur={handleBlur}
                meta={meta}
                shouldFilter
              />
            </div>
          ))}
        </div>
      )}

      {tableFields.length > 0 && (
        <div style={{ paddingBottom: 4, overflowY: 'auto' }}>
          {tableFields.map(table => (
            <CustomFieldTable
              key={table.id}
              table={table}
              meta={meta}
              setMeta={setMeta}
              metaRows={metaRows}
              setMetaRows={setMetaRows}
              canEdit={canEdit}
              handleValueChange={handleChange}
              onBlur={handleBlur}
            />
          ))}
        </div>
      )}

      {flowFields.length > 0 &&
        flowFields.map(customField => (
          <CustomFieldFlow
            key={customField.id}
            meta={meta}
            setMeta={setMeta}
            flow={customField.config?.id}
            objectType={customField.object_type}
            readOnly={readOnly}
            canEdit={canEdit}
            handleChange={handleChange}
            handleBlur={handleBlur}
          />
        ))}

      <AnimatePresence>
        {nextStages.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ overflow: 'auto', width: '100%' }}
          >
            {nextStages.map(stage => (
              <CustomFieldFlow
                key={stage}
                meta={meta}
                setMeta={setMeta}
                flow={stage}
                objectType={objectType}
                readOnly={readOnly}
                canEdit={canEdit}
                handleChange={handleChange}
                handleBlur={handleBlur}
                isNextStage
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </React.Fragment>
  )
}

export default CustomFieldFlow
